import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    EventEmitter,
    inject,
    input,
    Output,
    signal,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Customer } from '@models/customer';
import { CommonValidators } from 'app/shared/validators/common-validators';
import { PortalWidgetDetailsComponent } from '../../portal-widget-details/portal-widget-details.component';
import { RequiredInfoComponent } from 'app/shared/components/required-info/required-info.component';
import { Title } from '@models/title';
import { environment } from 'environments/environment';
import { CustomerService } from 'app/core/customer/customer.service';
import { toSignal } from '@angular/core/rxjs-interop';
import {
    STREET_REQUIRED,
    ZIP_REQUIRED,
    CITY_REQUIRED,
    CITY_TOO_SHORT,
    PHONE_REQUIRED,
    TITLE_REQUIRED,
} from '@config/error-messages.const';
import { TitlesService } from 'app/core/titles/titles.service';
import { finalize } from 'rxjs';
import { PortalActionButtonsComponent } from '../../portal-action-buttons/portal-action-buttons.component';

const TITLE_COMPANY_ID = environment.titleCompanyId;

@Component({
    selector: 'app-billing-address-edit',
    templateUrl: './billing-address-edit.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ReactiveFormsModule, PortalWidgetDetailsComponent, RequiredInfoComponent, PortalActionButtonsComponent],
})
export class BillingAddressEditComponent {
    private titleService = inject(TitlesService);
    private customerService = inject(CustomerService);
    private formBuilder = inject(FormBuilder);

    public customer = input.required<Customer>();

    @Output()
    public close = new EventEmitter<void>();

    private titles = toSignal(this.titleService.naturalPersonTitles$, { initialValue: [] });
    protected personTitles = computed(() => this.titles().filter((title: Title) => title.id !== TITLE_COMPANY_ID));
    protected isBusinessAccount = computed(() => this.customer().title.id === TITLE_COMPANY_ID);
    protected updating = signal(false);

    protected errorMessages = {
        title: TITLE_REQUIRED,
        street: STREET_REQUIRED,
        zip: ZIP_REQUIRED,
        phone: PHONE_REQUIRED,
        company: {
            default: CITY_REQUIRED,
            minlength: CITY_TOO_SHORT,
        },
        city: {
            default: CITY_REQUIRED,
            minlength: CITY_TOO_SHORT,
        },
    };

    protected formGroup = this.formBuilder.nonNullable.group({
        titleId: [null as string | null, Validators.required],
        company: [''],
        street: ['', [Validators.required, Validators.minLength(3)]],
        zip: ['', CommonValidators.zip],
        city: ['', [Validators.required, Validators.minLength(2)]],
        phone: ['', [Validators.required, ...CommonValidators.phone]],
    });

    public constructor() {
        effect(() => {
            const { title, ...rest } = this.customer();
            this.formGroup.reset({ titleId: '' + title.id, ...rest });
        });
        effect(() => {
            if (this.isBusinessAccount()) {
                this.formGroup.controls.company.addValidators([Validators.required, Validators.minLength(2)]);
            } else {
                this.formGroup.controls.company.addValidators([]);
            }
        });
    }

    protected handleSubmit(): void {
        if (this.formGroup.invalid) {
            return;
        }
        this.updating.set(true);
        const { titleId, ...rest } = this.formGroup.getRawValue();
        const newCustomer = {
            id: this.customer().id,
            firstName: this.customer().firstName,
            lastName: this.customer().lastName,
            fullName: this.customer().fullName,
            dateOfBirth: this.customer().dateOfBirth,
            mail: this.customer().mail,
            ...rest,
            title: this.titleService.titleFromId(parseInt(titleId, 10)),
        };
        this.customerService
            .updateCustomer(newCustomer)
            .pipe(finalize(() => this.updating.set(false)))
            .subscribe(() => {
                this.close.emit();
            });
    }
}
